import {DISCONTINUED_STATUS, LIMITED_AVAILABILITY_STATUS} from '~/assets/js/constants'

const STOCK_REVALIDATION_STATUSES = [LIMITED_AVAILABILITY_STATUS, DISCONTINUED_STATUS]

export function useRestrictedProducts() {

  const shouldRevalidateStock = (status: number): boolean => {
    return STOCK_REVALIDATION_STATUSES.includes(status)
  }

  const getStockAdaptedQuantity = (status: number, requestedQuantity: number, currentStockQuantity: number | null | undefined): number => {
    if (!shouldRevalidateStock(status)) {
      return requestedQuantity
    }
    const stock = currentStockQuantity ?? 0

    // (stock <= 0) keeps its quantity and is handled by the existing discontinued/limited rules.
    if (stock <= 0) {
      return requestedQuantity
    }
    return Math.min(requestedQuantity, stock)
  }

  const getSuccessorSku = (product: any): string | null => {
    const sku = product?.value?.salesAreas?.[0]?.successorSku ?? product?.salesAreas?.[0]?.successorSku ?? null
    return sku && sku !== '-' ? sku : null
  }

  const hasActiveSuccessor = (product: any): boolean => {
    const successorSku = getSuccessorSku(product)
    return successorSku !== null && successorSku !== undefined && successorSku !== ''
  }

  const isDiscontinuedAndNotEnabledForCheckout = (product: any, status: number, currentStockQuantity: number | null | undefined, isMarkedAsRequest: boolean): boolean => {
    if (status !== DISCONTINUED_STATUS) {
      return false
    }
    const hasSuccessor = hasActiveSuccessor(product)
    if (hasSuccessor) {
      return true
    }
    return (currentStockQuantity === 0 || currentStockQuantity === null || currentStockQuantity === undefined) && !isMarkedAsRequest
  }

  const isDiscontinuedWithSuccessorAndNoStock = (product: any, status: number, currentStockQuantity: number | null | undefined): boolean => {
    if (status !== DISCONTINUED_STATUS) {
      return false
    }

    const hasNoAtpStock = currentStockQuantity === 0 || currentStockQuantity === null || currentStockQuantity === undefined
    const hasSuccessor = hasActiveSuccessor(product)

    return hasNoAtpStock && hasSuccessor
  }

  const isDiscontinuedWithoutSuccessorAndNoStock = (product: any, status: number, currentStockQuantity: number | null | undefined): boolean => {
    if (status !== DISCONTINUED_STATUS) {
      return false
    }

    const hasNoAtpStock = currentStockQuantity === 0 || currentStockQuantity === null || currentStockQuantity === undefined
    const hasSuccessor = hasActiveSuccessor(product)

    return hasNoAtpStock && !hasSuccessor
  }

  const isDiscontinuedWithSuccessorAndStock = (product: any, status: number, currentStockQuantity: number | null | undefined): boolean => {
    if (status !== DISCONTINUED_STATUS) {
      return false
    }

    const hasNoAtpStock = currentStockQuantity === 0 || currentStockQuantity === null || currentStockQuantity === undefined
    const hasSuccessor = hasActiveSuccessor(product)

    return !hasNoAtpStock && hasSuccessor
  }

  const isDiscontinuedWithoutSuccessorAndStock = (product: any, status: number, currentStockQuantity: number | null | undefined): boolean => {
    if (status !== DISCONTINUED_STATUS) {
      return false
    }

    const hasNoAtpStock = currentStockQuantity === 0 || currentStockQuantity === null || currentStockQuantity === undefined
    const hasSuccessor = hasActiveSuccessor(product)

    return !hasNoAtpStock && !hasSuccessor
  }

  const isLimitedAvailabilityAndNoStock = (product: any, status: number, currentStockQuantity: number | null | undefined): boolean => {
    if (status !== LIMITED_AVAILABILITY_STATUS) {
      return false
    }

    const hasNoAtpStock = currentStockQuantity === 0 || currentStockQuantity === null || currentStockQuantity === undefined
    return hasNoAtpStock
  }

  const isCheckable = (product: any, status: number, currentStockQuantity: number | null | undefined, isMarkedAsRequest: boolean): boolean => {
    const withSuccessorNoStock = isDiscontinuedWithSuccessorAndNoStock(product, status, currentStockQuantity)
    const withoutSuccessorNoStock = isDiscontinuedWithoutSuccessorAndNoStock(product, status, currentStockQuantity)
    const withoutSuccessorStock = isDiscontinuedWithoutSuccessorAndStock(product, status, currentStockQuantity)
    const limitedAvailNoStock = isLimitedAvailabilityAndNoStock(product, status, currentStockQuantity)

    const uncheckableNoStockNoRequest = withoutSuccessorNoStock && !isMarkedAsRequest
    const uncheckableStockOnRequest = withoutSuccessorStock && isMarkedAsRequest

    return !(withSuccessorNoStock || uncheckableNoStockNoRequest || uncheckableStockOnRequest || limitedAvailNoStock)
  }

  return {
    hasActiveSuccessor,
    getSuccessorSku,
    shouldRevalidateStock,
    getStockAdaptedQuantity,
    isDiscontinuedAndNotEnabledForCheckout,
    isDiscontinuedWithSuccessorAndNoStock,
    isDiscontinuedWithoutSuccessorAndNoStock,
    isDiscontinuedWithSuccessorAndStock,
    isDiscontinuedWithoutSuccessorAndStock,
    isLimitedAvailabilityAndNoStock,
    isCheckable
  }
}
